import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { Container } from '@mui/system';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { ISingleAddressChangeApplication } from 'src/Types/module/applications/applications';
import { getStatusLabel } from 'src/Utils/Combined';
import GlobalLoader from 'src/components/SuspenseLoader/GlobalLoader';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from 'src/components/pdfAndImageView/CommonTypoGraphy';
import { url } from 'src/Helpers/utils/Constant';
import axios from 'axios';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import UpdateAddressChangeApp from './UpdateAddressChangeApp';
import OfflinePaymentCardInformation from '../../subModules/invoice/singleInvoice/OfflinePaymentCardInformation';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from 'src/components/CommonDetailItem/CommonDetailItem';

const labelWidth = { xs: '130px', sm: '150px', md: '170px' };

const SingleAddressApplication = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [application, setApplication] =
    useState<ISingleAddressChangeApplication>(
      {} as ISingleAddressChangeApplication
    );
  const [imageViewOpen, SetImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${url}/api/member/address-change-application/${id}`
        );

        if (data.success) {
          setApplication(data.data);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch (err: any) {
        setLoading(false);
      }
    })();
  }, [id]);

  const applicationDate = formatDate(
    application?.address_change_application_created_at,
    'MMM Do YYYY'
  );

  const statusLabel = application?.address_change_application_status
    ? getStatusLabel(application.address_change_application_status)
    : 'N/A';

  const civilAviationFile = application?.address_change_application_civil_aviation ? (
    <CommonTypoGraphy
      name='View Civil Aviation'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.address_change_application_civil_aviation);
      }}
    />
  ) : (
    'N/A'
  );

  const tradeLicenseFile = application?.address_change_application_trade_license ? (
    <CommonTypoGraphy
      name='View Trade License'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.address_change_application_trade_license);
      }}
    />
  ) : (
    'N/A'
  );

  const forwardingLetterFile = application?.address_change_application_form ? (
    <CommonTypoGraphy
      name='View Forwarding letter'
      onClick={() => {
        SetImageViewOpen(true);
        setImageFile(application.address_change_application_form);
      }}
    />
  ) : (
    'N/A'
  );

  return (
    <>
      <Helmet>
        <title>Atab - Address change application</title>
      </Helmet>
      <Container>
        {loading ? (
          <GlobalLoader />
        ) : (
          <Card>
            <CardHeader
              title='Address change application'
              action={
                <Button
                  onClick={() => setShowModal(true)}
                  variant='outlined'
                  size='small'
                >
                  Update
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {application ? (
                <Grid container spacing={2} pt={2}>
                  <CommonDetailItem
                    label='Company name'
                    value={formatValue(application.user_member_company_name)}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Application Date'
                    value={applicationDate}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Status'
                    value={statusLabel}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Email'
                    value={formatValue(
                      application.address_change_application_email
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Phone no'
                    value={formatValue(
                      application.address_change_application_phone
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Cellphone no'
                    value={formatValue(
                      application.address_change_application_cellphone
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Address'
                    value={formatValue(
                      application.address_change_application_addressline
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Division'
                    value={formatValue(application.division_name)}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='District'
                    value={formatValue(application.district_name)}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Thana'
                    value={formatValue(application.thana_name)}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Area'
                    value={formatValue(application.area_name)}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Police station'
                    value={formatValue(
                      application.address_change_application_police_station
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Postal code'
                    value={formatValue(
                      application.address_change_application_postal_code
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Civil Aviation File'
                    value={civilAviationFile}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Trade License File'
                    value={tradeLicenseFile}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Forwarding Letter'
                    value={forwardingLetterFile}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={6}
                    md={4}
                  />
                  <CommonDetailItem
                    label='Reject Reason'
                    value={formatValue(
                      application.address_change_application_reject_reason
                    )}
                    labelWidth={labelWidth}
                    xs={12}
                    sm={12}
                    md={12}
                  />
                </Grid>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.primary'
                  display='inline'
                  sx={{ pr: 1 }}
                  gutterBottom
                >
                  No Data found
                </Typography>
              )}
            </CardContent>

            {application?.paymentInfo && (
              <Grid item xs={12}>
                <Divider
                  sx={{
                    position: 'relative',
                    justifyContent: 'space-between',
                  }}
                >
                  <Chip
                    label='Payment Information'
                    size='small'
                    color='primary'
                  />
                  <Link
                    to={`/invoices/transactions/${application?.paymentInfo?.invoice_id}`}
                  >
                    <Chip
                      label='Go Invoice'
                      size='small'
                      sx={{
                        position: 'absolute',
                        right: 0,
                        background: '#D7E9FD',
                      }}
                    />
                  </Link>
                </Divider>

                <OfflinePaymentCardInformation
                  data={application?.paymentInfo}
                />
              </Grid>
            )}
          </Card>
        )}
      </Container>
      {application && (
        <ModalsWrapper
          setShowModal={setShowModal}
          showModal={showModal}
          modalData={{
            title: 'Update address change application',
          }}
        >
          <Box p={2}>
            <UpdateAddressChangeApp
              setShowModal={setShowModal}
              application={application}
              setApplication={setApplication}
            />
          </Box>
        </ModalsWrapper>
      )}
      <PdfAndImageView
        setOpen={SetImageViewOpen}
        open={imageViewOpen}
        file={imageFile}
      />
    </>
  );
};

export default SingleAddressApplication;
